import { useMemo, useState } from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/Avatar';
import { Badge } from '@/components/ui/Badge';
import { cn } from '@/lib/utils';

export interface PeoplePickerProfile {
  id: string;
  full_name: string | null;
  email: string;
  avatar_url?: string | null;
}

interface PeoplePickerProps {
  /** All candidate profiles (e.g. everyone in the org). */
  profiles: PeoplePickerProfile[];
  /** Currently selected profile ids. */
  value: string[];
  onChange: (next: string[]) => void;
  /** Profile ids that are pinned + can't be removed (e.g. the creator). */
  lockedIds?: string[];
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

/**
 * Multi-select people picker. Designed for the "Share with" field on
 * the New Project dialog and for the member editor on the project
 * detail page. Selected people are rendered as removable avatar
 * chips above the trigger button; the trigger opens a search-and-pick
 * dropdown with the standard avatar / name / email row layout.
 *
 * `lockedIds` lets the dialog auto-include the creator (the DB trigger
 * adds them as a member regardless, but rendering them as a locked
 * chip makes the "you can never un-share with yourself" rule visible
 * in the UI).
 */
export function PeoplePicker({
  profiles,
  value,
  onChange,
  lockedIds = [],
  placeholder = 'Add people…',
  disabled = false,
  className,
}: PeoplePickerProps) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');

  const profileById = useMemo(() => {
    const map = new Map<string, PeoplePickerProfile>();
    for (const p of profiles) map.set(p.id, p);
    return map;
  }, [profiles]);

  const selected = useMemo(
    () => value.map((id) => profileById.get(id)).filter((p): p is PeoplePickerProfile => !!p),
    [value, profileById],
  );

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return profiles;
    return profiles.filter((p) =>
      `${p.full_name ?? ''} ${p.email}`.toLowerCase().includes(q),
    );
  }, [profiles, query]);

  const toggle = (id: string) => {
    if (lockedIds.includes(id)) return;
    if (value.includes(id)) onChange(value.filter((v) => v !== id));
    else onChange([...value, id]);
  };

  const remove = (id: string) => {
    if (lockedIds.includes(id)) return;
    onChange(value.filter((v) => v !== id));
  };

  return (
    <div className={cn('space-y-2', className)}>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {selected.map((p) => {
            const locked = lockedIds.includes(p.id);
            return (
              <Badge
                key={p.id}
                variant="secondary"
                className="flex items-center gap-1.5 rounded-full py-0.5 pl-0.5 pr-2 text-xs font-normal"
              >
                <Avatar className="h-5 w-5">
                  {p.avatar_url && <AvatarImage src={p.avatar_url} alt="" />}
                  <AvatarFallback className="text-[9px]">
                    {initialsOf(p)}
                  </AvatarFallback>
                </Avatar>
                <span className="truncate text-foreground">
                  {p.full_name ?? p.email}
                </span>
                {!locked && !disabled && (
                  <button
                    type="button"
                    onClick={() => remove(p.id)}
                    className="ml-0.5 rounded-full text-muted-foreground hover:text-foreground"
                    aria-label={`Remove ${p.full_name ?? p.email}`}
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </Badge>
            );
          })}
        </div>
      )}

      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            className={cn(
              'flex h-10 w-full items-center justify-between rounded-lg border border-input bg-card px-3 py-2 text-sm text-muted-foreground transition-colors',
              'focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 focus:ring-offset-background',
              'disabled:cursor-not-allowed disabled:opacity-50',
            )}
          >
            <span>{placeholder}</span>
            <ChevronDown className="h-4 w-4 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-72 p-0">
          <div className="flex items-center gap-2 border-b border-border px-3 py-2">
            <Search className="h-4 w-4 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search people…"
              className="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground focus:outline-none"
            />
          </div>
          <div className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <div className="px-3 py-6 text-center text-xs text-muted-foreground">
                No people found.
              </div>
            ) : (
              filtered.map((p) => {
                const isSelected = value.includes(p.id);
                const locked = lockedIds.includes(p.id);
                return (
                  <button
                    key={p.id}
                    type="button"
                    onClick={() => toggle(p.id)}
                    disabled={locked}
                    className={cn(
                      'flex w-full items-center gap-2.5 rounded-md px-2 py-1.5 text-left text-sm hover:bg-secondary/60',
                      locked && 'cursor-not-allowed opacity-60',
                    )}
                  >
                    <Avatar className="h-7 w-7">
                      {p.avatar_url && <AvatarImage src={p.avatar_url} alt="" />}
                      <AvatarFallback className="text-[10px]">
                        {initialsOf(p)}
                      </AvatarFallback>
                    </Avatar>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm text-foreground">
                        {p.full_name ?? p.email}
                      </div>
                      {p.full_name && (
                        <div className="truncate text-xs text-muted-foreground">
                          {p.email}
                        </div>
                      )}
                    </div>
                    <span
                      className={cn(
                        'flex h-4 w-4 shrink-0 items-center justify-center rounded border border-border',
                        isSelected
                          ? 'border-primary bg-primary text-primary-foreground'
                          : 'opacity-60',
                      )}
                    >
                      {isSelected && <Check className="h-3 w-3" />}
                    </span>
                  </button>
                );
              })
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function initialsOf(p: PeoplePickerProfile): string {
  const source = p.full_name ?? p.email;
  return (
    source
      .split(/[.\s@]/)
      .filter(Boolean)
      .slice(0, 2)
      .map((s) => s[0]?.toUpperCase() ?? '')
      .join('') || '?'
  );
}
